/**
 * <s-delivery-estimate> — live delivery window on the product page.
 * arrives = today + total-min … today + total-max business days (Mon–Fri;
 * public holidays not handled). Writes formatted dates into
 * [data-date="arrive-min|arrive-max"] and reveals [data-dates].
 * Server-rendered fallback text stays when JS is off.
 * Plays a one-off progress-line fill (skipped with reduced motion).
 */
class SDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const totalMin = Number(this.dataset.totalMin) || 7;
    const totalMax = Number(this.dataset.totalMax) || 12;

    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const arriveMin = this.#addBusinessDays(today, totalMin);
    const arriveMax = this.#addBusinessDays(today, totalMax);

    const locale = document.documentElement.lang || 'pt-PT';
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const values = {
      'arrive-min': fmt.format(arriveMin),
      'arrive-max': fmt.format(arriveMax),
    };
    this.querySelectorAll('[data-date]').forEach((node) => {
      const value = values[node.dataset.date];
      if (value) node.textContent = value;
    });
    this.querySelectorAll('[data-dates]').forEach((node) => node.removeAttribute('hidden'));
    this.querySelectorAll('[data-fallback]').forEach((node) => node.setAttribute('hidden', ''));

    this.#animate();
  }

  #addBusinessDays(start, days) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return date;
  }

  #animate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const line = this.querySelector('[data-progress]');
    const dots = this.querySelectorAll('[data-dot]');
    const run = () => {
      line?.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
        duration: 1400,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      });
      dots.forEach((dot, i) => {
        dot.animate(
          [
            { transform: 'scale(0.4)', opacity: 0.3 },
            { transform: 'scale(1.25)', opacity: 1, offset: 0.6 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          { duration: 500, delay: i * 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' },
        );
      });
    };
    if (!('IntersectionObserver' in window)) return run();
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        run();
      }
    }, { threshold: 0.6 });
    observer.observe(this);
  }
}

if (!customElements.get('s-delivery-estimate')) {
  customElements.define('s-delivery-estimate', SDeliveryEstimate);
}
